import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  PLAYER,
  BULLET,
  MULTISHOT,
  PLAYER_FX,
  DASH,
  INPUT_REGIONS,
  type BulletBuffKind,
} from '../config/GameConfig';
import Bullet from './Bullet';

export interface DamageResult {
  died: boolean;
  blocked: boolean;
  byShield: boolean;
}

/**
 * Player —— 玩家载具（坦克）
 *
 * 升级系统会读写的属性：
 *   shootingDelay   射击间隔（ms）
 *   bulletDamage    子弹伤害
 *   bulletCount     同时发射子弹数
 *   bulletPierce    子弹穿透次数
 *   bulletHoming    是否启用跟踪
 *   bulletRicochet  子弹反弹（命中后向上重新发射的次数）
 *   critChance      暴击概率 [0, 1]
 *   critMul         暴击倍率（默认 2）
 *   lifestealChance 击杀回血概率 [0, 1]
 *   maxHp / hp      生命值
 *   shieldCharges   护盾层数
 *   coins           金币数
 *
 *   bulletSpread    霰弹专精：每发子弹随机偏转 ±N 弧度（0=精准）
 *   berserk         速射狂热：射速 ≤ 100ms 时伤害 ×1.3（在 CombatSystem 应用）
 *   shieldExplode   盾爆：护盾层被打碎时炸 120 半径 AOE（由 MainScene 在 byShield 命中时触发）
 */
export default class Player extends Phaser.GameObjects.Container {
  declare body: Phaser.Physics.Arcade.Body;

  shootingDelay: number;
  bulletDamage: number;
  bulletCount: number;
  bulletPierce: number;
  bulletHoming: boolean;
  bulletRicochet: number;
  /** 元素弹药 buff（火/冰/闪电），到 _bulletBuffUntil 时清空 */
  bulletBuff: BulletBuffKind | null = null;
  private _bulletBuffUntil = 0;
  critChance: number;
  critMul: number;
  lifestealChance: number;
  maxHp: number;
  hp: number;
  shieldCharges: number;
  coins: number;
  /** 霰弹专精：子弹发射时随机偏转 ±此弧度（默认 0 = 直线） */
  bulletSpread = 0;
  /** 速射狂热：true 表示开启低延迟伤害加成 */
  berserk = false;
  /** 盾爆：true 表示护盾被打碎时触发 AOE */
  shieldExplode = false;

  bulletGroup: Phaser.Physics.Arcade.Group;

  private _sprite: Phaser.GameObjects.Image;
  private _shadow: Phaser.GameObjects.Ellipse;
  /** 多层护盾：每多一层就多一圈，外层颜色更亮 */
  private _shieldRings: Phaser.GameObjects.Arc[] = [];
  private _lastShotAt = 0;
  /** 当前是否有目标点（手指按住或刚抬起还没走到） */
  private _hasTarget = false;
  /** 目标点：玩家会以 PLAYER.moveSpeed 朝它移动直到到达；不再瞬移 */
  private _targetX = 0;
  private _targetY = 0;
  /** 受击后的无敌截止时间戳 (performance.now()) */
  _invincibleUntil = 0;
  /** 短闪冷却结束的时间戳（performance.now()）。<= now 表示可用 */
  private _dashReadyAt = 0;
  /**
   * HUD 上不应被识别为"移动指令"的圆形区域（如闪现按钮、暂停按钮）。
   * 由 MainScene/HUD 在创建按钮后通过 addInputDeadZone 注册；
   * setTarget 检测命中其中任何一个就跳过。
   */
  private _inputDeadZones: Array<{ x: number; y: number; r: number }> = [];

  constructor(scene: Phaser.Scene, bulletGroup: Phaser.Physics.Arcade.Group) {
    // 进入游戏默认在屏幕正中央 —— 自由战场玩法下，居中起手最公平：
    // 玩家可以同时关注上方刷怪 + 四周 Boss 子弹，手指点哪都能开始拖
    const x = GAME_WIDTH / 2;
    const y = GAME_HEIGHT / 2;
    super(scene, x, y);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.setDepth(50);

    this._shadow = scene.add.ellipse(0, PLAYER.height / 2 - 4, PLAYER.width * 0.9, 8, 0x000000, 0.25);
    this._sprite = scene.add.image(0, 0, 'player-tank');
    this.add([this._shadow, this._sprite]);

    this.body.setSize(PLAYER.width, PLAYER.height);
    this.body.setOffset(-PLAYER.width / 2, -PLAYER.height / 2);
    this.body.setCollideWorldBounds(true);
    this.body.setAllowGravity(false);
    this.body.setImmovable(true);

    this.shootingDelay = PLAYER.initialFireDelay;
    this.bulletDamage = BULLET.damage;
    this.bulletCount = MULTISHOT.initial;
    this.bulletPierce = 0;
    this.bulletHoming = false;
    this.bulletRicochet = 0;
    this.critChance = 0;
    this.critMul = 2;
    this.lifestealChance = 0;
    this.maxHp = 5;
    this.hp = this.maxHp;
    this.shieldCharges = 0;
    this.coins = 0;

    this.bulletGroup = bulletGroup;

    this._bindInput(scene);
  }

  private _bindInput(scene: Phaser.Scene): void {
    const input = scene.input;
    // 多指支持：默认 Phaser 只追 1 个 pointer，开 2 个让"左手拖动 + 右手点技能"成立
    input.addPointer(2);

    /**
     * 进入场景时若手指/鼠标已按住（点"开始游戏"按钮没松开），第一帧 pointermove 会
     * 立刻把按钮位置当移动目标，玩家就被拽过去。靠这个 flag 在第一次 pointerup 之前
     * 屏蔽所有 input：要求玩家"先抬手再按下"才算有效操作。
     */
    let inputArmed = false;

    /**
     * 输入分区（v4 右下角集中版）：
     *   - 落点 y < INPUT_REGIONS.hudHeight：忽略（顶部 HUD）
     *   - 落点 x >= GAME_WIDTH - skillZoneW 且 y >= GAME_HEIGHT - skillZoneH：
     *     右下角技能矩形，忽略（按钮自己处理）
     *   - 其余 = 战场区，拖动设玩家目标
     *
     * 拖动会话锁 moveSessionPointerId：pointerdown 落在战场区时记 pointer.id，
     * 之后该指针的 move 事件无视区域检查；其他 pointer 的 move 仍走完整检查。
     */
    let moveSessionPointerId = -1;

    /** 区域检查 —— pointerdown 时调用，决定要不要进入移动会话 */
    const isInMoveRegion = (p: Phaser.Input.Pointer): boolean => {
      if (p.y < INPUT_REGIONS.hudHeight) return false;
      // 右下角技能矩形
      if (
        p.x >= GAME_WIDTH - INPUT_REGIONS.skillZoneW &&
        p.y >= GAME_HEIGHT - INPUT_REGIONS.skillZoneH
      ) return false;
      // HUD 死区（顶部圆按钮）
      for (const z of this._inputDeadZones) {
        const dx = p.x - z.x;
        const dy = p.y - z.y;
        if (dx * dx + dy * dy <= z.r * z.r) return false;
      }
      return true;
    };

    /** 真正写入目标点 —— 调用方已确认该次输入应处理 */
    const writeTarget = (p: Phaser.Input.Pointer): void => {
      const halfW = PLAYER.width / 2;
      const halfH = PLAYER.height / 2;
      // 远端点击给一点向上偏移，让玩家走到点位时手指不会完全挡住车身
      const yBias = -PLAYER.height * 0.3;
      this._targetX = Phaser.Math.Clamp(p.x, halfW, GAME_WIDTH - halfW);
      this._targetY = Phaser.Math.Clamp(p.y + yBias, halfH, GAME_HEIGHT - halfH);
      this._hasTarget = true;
    };

    // 场景刚进来时如果鼠标/手指未按住，立刻 arm；否则等第一次 pointerup 再 arm
    if (!input.activePointer.isDown) {
      inputArmed = true;
    }
    input.on('pointerup', (p: Phaser.Input.Pointer) => {
      inputArmed = true;
      if (p.id === moveSessionPointerId) {
        moveSessionPointerId = -1;
      }
    });
    input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (!inputArmed) return;
      if (!isInMoveRegion(p)) return;
      moveSessionPointerId = p.id;
      writeTarget(p);
    });
    input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown || !inputArmed) return;
      // 已锁定移动会话的指针：无视区域检查（允许滑过分割线后继续 follow）
      if (p.id === moveSessionPointerId) {
        writeTarget(p);
      }
    });
    // 抬手不清目标点：让玩家走完最后一段，避免"短点一下就停在原地"
  }

  /**
   * 每帧由 MainScene 调用：朝目标点匀速移动一步，到达就清掉目标。
   * 不再用 setVelocity（玩家是 immovable body），直接改 x/y 然后 clamp 屏内。
   */
  stepMove(delta: number): void {
    if (!this._hasTarget) return;
    const dx = this._targetX - this.x;
    const dy = this._targetY - this.y;
    const dist = Math.hypot(dx, dy);
    if (dist <= PLAYER.arriveEps) {
      this._hasTarget = false;
      return;
    }
    const step = (PLAYER.moveSpeed * delta) / 1000;
    if (step >= dist) {
      // 这一帧能走完
      this.x = this._targetX;
      this.y = this._targetY;
      this._hasTarget = false;
    } else {
      this.x += (dx / dist) * step;
      this.y += (dy / dist) * step;
    }
  }

  /**
   * 同步护盾环：根据 shieldCharges 数量增删圆环。
   * 第 N 层环：半径 = 基础半径 + N × 6，颜色更亮 alpha 更低。
   */
  syncShieldRing(): void {
    const need = Math.max(0, this.shieldCharges);
    // 多了：补环
    while (this._shieldRings.length < need) {
      const idx = this._shieldRings.length;
      const r = PLAYER.width * 0.95 + idx * 7;
      const ring = this.scene.add
        .circle(this.x, this.y, r, 0x4cd2ff, 0)
        .setStrokeStyle(3, 0x4cd2ff, 0.85 - idx * 0.08)
        .setDepth(49 - idx);
      this._shieldRings.push(ring);
    }
    // 少了：销毁多余的环
    while (this._shieldRings.length > need) {
      const ring = this._shieldRings.pop();
      ring?.destroy();
    }
    // 跟随玩家
    for (const r of this._shieldRings) {
      r.setPosition(this.x, this.y);
    }
  }

  /**
   * 自动射击 + 多重射击编队（详见 GameConfig.MULTISHOT）。
   * 返回 true 表示这一帧真的发射了（外层用来播放音效）。
   */
  tryShoot(now: number, fireRateMul = 1): boolean {
    const eff = this.shootingDelay * fireRateMul;
    if (now - this._lastShotAt < eff) return false;
    this._lastShotAt = now;

    const total = Math.max(1, Math.min(MULTISHOT.max, this.bulletCount));
    const perRow = MULTISHOT.perRow;
    const muzzleY = this.y - PLAYER.height / 2 - 4;

    for (let i = 0; i < total; i++) {
      const row = Math.floor(i / perRow);
      const indexInRow = i % perRow;
      const remaining = total - row * perRow;
      const inThisRow = Math.min(perRow, remaining);
      const offsetX = (indexInRow - (inThisRow - 1) / 2) * MULTISHOT.colGap;
      const offsetY = -row * MULTISHOT.rowGap;

      const bullet = new Bullet(this.scene, this.x + offsetX, muzzleY + offsetY);
      bullet.damage = this.bulletDamage;
      bullet.pierce = this.bulletPierce;
      bullet.homing = this.bulletHoming;
      bullet.ricochet = this.bulletRicochet;
      // 元素 buff 期间，子弹带 effect 标志 + 着色（命中处理由 MainScene 分支）
      if (this.bulletBuffMsLeft() > 0 && this.bulletBuff) {
        bullet.effect = this.bulletBuff;
        // 三种元素的 tint：与 BULLET_BUFF.tint 对齐（避免再 import，硬编码常量）
        const tint = this.bulletBuff === 'fire' ? 0xff6a3c
          : this.bulletBuff === 'ice' ? 0x6ad6ff
          : 0xc0a8ff;
        bullet.setTint(tint);
      }
      this.bulletGroup.add(bullet);
      // 霰弹专精：每发独立 ±spread 偏转（不是整组扫角，而是每发各自抖动）
      if (this.bulletSpread > 0) {
        const ang = -Math.PI / 2 + (Math.random() - 0.5) * 2 * this.bulletSpread;
        const sp = Math.abs(BULLET.speed); // BULLET.speed 是负数（向上），这里取模长再分解
        bullet.body.setVelocity(Math.cos(ang) * sp, Math.sin(ang) * sp);
      } else {
        bullet.body.setVelocity(0, BULLET.speed);
      }
      bullet.body.setAllowGravity(false);
    }
    return true;
  }

  /** 受伤；返回 { died, blocked, byShield } */
  takeDamage(amount = 1): DamageResult {
    const now = performance.now();
    if (now < this._invincibleUntil) {
      return { died: false, blocked: true, byShield: false };
    }
    if (this.shieldCharges > 0) {
      // 取最外层环来播一下"碎裂"的弹缩动画，再扣层数
      const outer = this._shieldRings[this._shieldRings.length - 1];
      this.shieldCharges -= 1;
      this._invincibleUntil = now + PLAYER_FX.invincibleMs;
      if (outer) {
        this.scene.tweens.add({
          targets: outer,
          scale: { from: 1.3, to: 1 },
          alpha: { from: 1, to: 0 },
          duration: 220,
          onComplete: () => outer.destroy(),
        });
        // 立即从数组里移除（syncShieldRing 不会再去重建），避免下一帧又创建一个
        this._shieldRings.pop();
      }
      return { died: false, blocked: true, byShield: true };
    }

    this.hp -= amount;
    this._invincibleUntil = now + PLAYER_FX.invincibleMs;
    this.scene.tweens.add({
      targets: this._sprite,
      alpha: { from: 1, to: 0.3 },
      duration: 80,
      yoyo: true,
      repeat: 3,
    });
    return { died: this.hp <= 0, blocked: false, byShield: false };
  }

  addCoins(n: number): void {
    this.coins += n;
  }

  /**
   * 应用一个元素弹药 buff（拾取宝箱有概率换成这个）
   * 互斥：新 buff 直接覆盖旧的，不叠加时长
   */
  applyBulletBuff(kind: BulletBuffKind, durationMs: number): void {
    this.bulletBuff = kind;
    this._bulletBuffUntil = performance.now() + durationMs;
  }

  /** 当前 buff 剩余 ms（<=0 表示无 buff） */
  bulletBuffMsLeft(): number {
    if (!this.bulletBuff) return 0;
    const left = this._bulletBuffUntil - performance.now();
    if (left <= 0) {
      this.bulletBuff = null;
      return 0;
    }
    return left;
  }

  /** 注册一个圆形 HUD 死区：玩家点这里不会触发"移动到这里" */
  addInputDeadZone(x: number, y: number, r: number): void {
    this._inputDeadZones.push({ x, y, r });
  }

  /**
   * 短闪：朝当前移动目标方向闪现 DASH.distance 像素 + 短暂无敌。
   * 没有目标点（站着没动）时朝屏幕上方闪（一般是要躲下方弹幕）。
   * 返回 true 表示这次成功触发了（用于音效 / HUD 按钮反馈）。
   */
  tryDash(): boolean {
    const now = performance.now();
    if (now < this._dashReadyAt) return false;
    this._dashReadyAt = now + DASH.cooldownMs;

    // 闪现方向：优先用目标点的方向；没有目标 / 距离太近就朝上
    let dx = 0;
    let dy = -1;
    if (this._hasTarget) {
      const tdx = this._targetX - this.x;
      const tdy = this._targetY - this.y;
      const td = Math.hypot(tdx, tdy);
      if (td > 4) {
        dx = tdx / td;
        dy = tdy / td;
      }
    }

    const halfW = PLAYER.width / 2;
    const halfH = PLAYER.height / 2;
    this.x = Phaser.Math.Clamp(this.x + dx * DASH.distance, halfW, GAME_WIDTH - halfW);
    this.y = Phaser.Math.Clamp(this.y + dy * DASH.distance, halfH, GAME_HEIGHT - halfH);
    // 顺手把目标点拉到当前位置，避免闪完又走回去
    this._targetX = this.x;
    this._targetY = this.y;
    this._hasTarget = false;

    // 短暂无敌
    this._invincibleUntil = Math.max(this._invincibleUntil, now + DASH.invincibleMs);

    // 视觉：sprite 闪一下 + 留 3 个残影
    this.scene.tweens.add({
      targets: this._sprite,
      alpha: { from: 0.3, to: 1 },
      duration: 90,
      yoyo: true,
      repeat: 2,
    });
    for (let i = 1; i <= 3; i++) {
      const ghost = this.scene.add
        .image(this.x - dx * (i * 22), this.y - dy * (i * 22), 'player-tank')
        .setAlpha(0.5 - i * 0.1)
        .setTint(0x4cd2ff)
        .setDepth(48);
      this.scene.tweens.add({
        targets: ghost,
        alpha: 0,
        duration: 280,
        onComplete: () => ghost.destroy(),
      });
    }

    return true;
  }

  /** 闪现冷却剩余 ms，<=0 表示可用 */
  dashCooldownMs(): number {
    return Math.max(0, this._dashReadyAt - performance.now());
  }
  /** 闪现冷却进度比例 [0..1]，0 = 刚用过，1 = 已就绪 */
  dashCooldownRatio(): number {
    const left = this.dashCooldownMs();
    if (left <= 0) return 1;
    return 1 - left / DASH.cooldownMs;
  }
}
